import { useCallback } from 'react';
import {
    alertApiRef,
    discoveryApiRef,
    fetchApiRef,
    useApi,
} from '@backstage/core-plugin-api';

/**
 * Asks the GitLabEntityProvider to read the project of the given entity
 * again, the way a push to it is read.
 *
 * The catalog's own entity refresh only re-processes what the provider stored
 * during its last run, so for entities from a catalog-info.yaml in GitLab it
 * never picks up a change before the next scheduled run - this is what
 * actually makes the refresh button do something for them. The backend reads
 * the same project at most once a minute and answers 429 in between.
 *
 * What it tells the reader is the same as for the CMDB: the button is the
 * refresh of the entity, whichever source it re-reads.
 */
export function useGitLabRefresh(): (entityRef: string) => Promise<void> {
    const discoveryApi = useApi(discoveryApiRef);
    const fetchApi = useApi(fetchApiRef);
    const alertApi = useApi(alertApiRef);

    return useCallback(
        async (entityRef: string) => {
            const baseUrl = await discoveryApi.getBaseUrl('catalog');
            const response = await fetchApi.fetch(
                `${baseUrl}/rtl/gitlab/refresh`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ entityRef: entityRef }),
                },
            );

            if (response.status === 429) {
                alertApi.post({
                    message:
                        'A refresh was started less than a minute ago, please try again shortly',
                    severity: 'info',
                    display: 'transient',
                });
                return;
            }

            if (!response.ok) {
                throw new Error(
                    `Refresh failed with status ${response.status}`,
                );
            }

            alertApi.post({
                message: 'Refresh started, the data follows in a moment',
                severity: 'info',
                display: 'transient',
            });
        },
        [discoveryApi, fetchApi, alertApi],
    );
}
